<template>
  <div id="main-preview">
    <h1 class="title-preview my-5">{{ roomName }}</h1>

    <!-- Error handler -->
    <ErrorDialog :error="error" v-on:close-error="closeError" />

    <div id="video-preview">
      <!-- Audio -->
      <audio class="audio-preview" style="opacity: 0"></audio>

      <!-- Video -->
      <video class="video-preview-render" autoplay muted playsinline></video>

      <!-- Buttons -->
      <div class="video-preview-btn">
        <div class="level-indicator-block">
          <div class="level-indicator-max">
            <div class="level-indicator"></div>
          </div>
        </div>
        <div>
          <ButtonDeviceActivation
            kind="audio"
            v-on:toggle-device="toggleDevice"
            :activeDevice="activeDevicesAudio"
            :disabled="disabledAudioButton"
          />
          <ButtonDeviceActivation
            kind="video"
            v-on:toggle-device="toggleDevice"
            :activeDevice="activeDevicesVideo"
            :disabled="disabledVideoButton"
          />
        </div>
        <div>
          <SelectDevicesModal />
        </div>
      </div>
    </div>

    <div class="btn-block">
      <v-btn :disabled="disabledAuthRoom" class="ma-2 text-min" color="primary" @click="joinRoom">{{
        $t('rooms.preview.join')
      }}</v-btn>
      <v-btn v-if="authenticated" class="ma-2 text-min" @click="back">{{ $t('rooms.preview.back') }}</v-btn>
    </div>
  </div>
</template>

<script>
import { createLocalTracks } from 'twilio-video'
import { mapState } from 'vuex'
import { twilioMixin } from '@/mixins/twilioMixin.js'
import { callVideoActions } from '@/mixins/callVideoActions.js'
import micLevel from '@/script/micLevel.js'

// Component
import ButtonDeviceActivation from './buttons/ButtonDeviceActivation.vue'
import ErrorDialog from './ErrorDialog.vue'
import SelectDevicesModal from './SelectDevicesModal.vue'

export default {
  name: 'PreviewVideoCall',

  mixins: [twilioMixin, callVideoActions],

  props: {
    authenticated: Boolean,
    room: Object
  },

  computed: {
    ...mapState({
      roomDevices: (state) => Object.assign({}, state.roomDevices),
      roomDevicesVideo: (state) => state.roomDevices.video,
      roomDevicesAudio: (state) => state.roomDevices.audio,
      activeDevices: (state) => state.activeDevices,
      activeDevicesAudio: (state) => state.activeDevices.audio,
      activeDevicesVideo: (state) => state.activeDevices.video
    }),

    disabledAuthRoom() {
      return !this.localTracksData.video || !this.localTracksData.audio
    },

    roomName() {
      return this.room && this.room.session_name
    }
  },

  components: {
    ButtonDeviceActivation,
    ErrorDialog,
    SelectDevicesModal
  },

  data() {
    return {
      error: {
        name: null,
        isDisplayed: false
      },
      disabledVideoButton: true,
      disabledAudioButton: true,
      localTracksData: { video: null, audio: null }
    }
  },

  async created() {
    await this.setInitialDevices('video')
    await this.setInitialDevices('audio')

    if (!this.roomDevicesVideo || !this.roomDevicesAudio) return

    const options = {
      video: { deviceId: this.roomDevicesVideo },
      audio: { deviceId: this.roomDevicesAudio }
    }

    this.createTrack(options)
  },

  watch: {
    roomDevices: {
      deep: true,
      async handler(newIds, oldIds) {
        const container = document.getElementById('video-preview')

        let kind = null
        let id = null

        if (oldIds && oldIds.video && newIds.video !== oldIds.video) {
          kind = 'video'
          id = newIds.video
        }

        if (oldIds && oldIds.audio && newIds.audio !== oldIds.audio) {
          kind = 'audio'
          id = newIds.audio
        }

        if (kind && id) {
          // Create a new LocalTrack from the given Device ID.
          const [track] = await createLocalTracks({ [kind]: { deviceId: id } })

          // Stop the previous LocalTrack, if present.
          this.stopLocalTrack(kind)

          // Attach new track to the DOM
          this.attachNewTrack(kind, container, track)

          // Save new track
          this.localTracksData[kind] = track

          // Display new mic level if kink === audio
          if (kind === 'audio') {
            this.setMicLevel(track)
          }

          // Display device or not from the previous state
          if (this.activeDevices[kind]) {
            this.localTracksData[kind].enable()
          } else {
            this.localTracksData[kind].disable()
          }
        }
      }
    }
  },

  methods: {
    createTrack(options) {
      createLocalTracks(options)
        .then((localTracks) => {
          localTracks.forEach((track) => {
            const container = document.getElementById('video-preview')

            if (track) {
              this.localTracksData[track.kind] = track
              this.commitToggleDevice(track.kind, track.isEnabled)
              this.attachNewTrack(track.kind, container, track)
            }

            if (track.kind === 'video') {
              this.disabledVideoButton = false
            }

            if (track.kind === 'audio') {
              this.setMicLevel(track)
              this.disabledAudioButton = false
            }
          })
        })
        .catch(this.handleMediaError)
    },

    async setInitialDevices(kind) {
      let local = null
      let devices = null
      let filteredDevices = null
      let deviceId = null

      local = localStorage.getItem(`${kind}DeviceId`)

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        devices = await navigator.mediaDevices.enumerateDevices()
        filteredDevices = devices.filter((device) => device.kind === `${kind}input`)
      } catch (err) {
        this.handleMediaError(err)
        return
      }

      if (filteredDevices.length === 0) {
        return
      } else {
        if (!local) {
          // On renvoie la premiere camera qui doit etre la default
          deviceId = filteredDevices[0].deviceId
        } else {
          const previousDeviceUsed = filteredDevices.find((el) => el.deviceId === local)
          deviceId = (previousDeviceUsed && previousDeviceUsed.deviceId) || filteredDevices[0].deviceId
        }

        this.$store.commit('CHANGE_DEVICE_ID', {
          kind: kind,
          deviceId: deviceId
        })
        localStorage.setItem(`${kind}DeviceId`, deviceId)
      }
    },

    setMicLevel(track) {
      const levelIndicatorMax = document.querySelector('.level-indicator-max')
      const levelIndicator = document.querySelector('.level-indicator')
      const maxLevel = Number(levelIndicatorMax.offsetHeight)
      const stream = new MediaStream([track.mediaStreamTrack])
      micLevel(stream, maxLevel, (level) => levelIndicator.setAttribute('style', `height:${level}px`))
    },

    attachNewTrack(kind, container, track) {
      const el = container.getElementsByTagName(kind)[0]
      track.attach(el)
    },

    stopLocalTrack(kind) {
      if (this.localTracksData[kind]) {
        this.localTracksData[kind].stop()
        this.localTracksData[kind] = null
      }
    },

    stopLocalTracks() {
      const tracksType = ['video', 'audio']

      tracksType.forEach((el) => {
        this.stopLocalTrack(el)
      })
    },

    joinRoom() {
      this.$emit('join-room')
      this.stopLocalTracks()
    },

    back() {
      this.$emit('back-room')
      this.stopLocalTracks()
    },

    closeError() {
      this.error.name = null
      this.error.isDisplayed = false
    },

    handleMediaError(error) {
      this.error.name = error.name
      this.error.isDisplayed = true
    },

    toggleDevice(kind) {
      const local = this.localTracksData[kind]

      if (!local) return

      if (local.isEnabled) {
        local.disable()
        this.commitToggleDevice(kind, false)
      } else {
        local.enable()
        this.commitToggleDevice(kind, true)
      }
    }
  }
}
</script>

<style lang="sass" scoped>
#main-preview
  margin: 0 auto
  max-width: 600px

.title-preview
  text-align: center
  opacity: .87

#video-preview
  position: relative
  background-color: rgba(0, 0, 0, .9)
  max-width: 600px
  width: 100%
  height: 400px
  margin: 25px 0
  border: 1px solid #eee
  overflow: hidden
  display: flex
  justify-content: center
  align-items: center
  border-radius: 25px

.video-preview-render
  transform: scaleX(-1)
  width: 100%
  height: auto

.video-preview-btn
  position: absolute
  width: 100%
  height: 100px
  background-color: rgba(0, 0, 0, .4)
  bottom: 0
  display: flex
  justify-content: space-between
  align-items: center
  padding: 0 15px

.btn-block
  display: flex
  justify-content: center

.level-indicator-block
  width: 56px
  height: 56px
  display: flex
  justify-content: center
  align-items: flex-end

.level-indicator-max
  position: relative
  height: 50px
  width: 5px
  border-radius: 25px
  background-color: #333

.level-indicator
  position: absolute
  bottom: 0
  background-color: green
  width: 100%

.text-min
  text-transform: inherit !important
  font-family: 'Fira Sans', sans-serif
</style>
